import React, { useMemo, useCallback } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { cn } from "@/lib/utils";

export interface TableColumn<T = any> {
  key: string;
  title: string;
  dataIndex?: keyof T;
  render?: (value: any, record: T, index: number) => React.ReactNode;
  sortable?: boolean;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

export interface TablePagination {
  current: number;
  pageSize: number;
  total: number;
  showSizeChanger?: boolean;
  pageSizeOptions?: number[];
}

export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

interface OptimizedTableProps<T = any> {
  columns: TableColumn<T>[];
  data: T[];
  loading?: boolean;
  pagination?: TablePagination;
  onPaginationChange?: (page: number, pageSize: number) => void;
  sortConfig?: SortConfig;
  onSortChange?: (sortConfig: SortConfig | null) => void;
  rowKey?: keyof T | ((record: T) => string);
  className?: string;
  size?: 'small' | 'middle' | 'large';
  emptyText?: string;
  onRow?: (record: T, index: number) => React.HTMLAttributes<HTMLTableRowElement>;
}

export function OptimizedTable<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  pagination,
  onPaginationChange,
  sortConfig,
  onSortChange,
  rowKey = 'id',
  className,
  size = 'middle',
  emptyText = "لا توجد بيانات",
  onRow
}: OptimizedTableProps<T>) {
  const displayData = useMemo(() => {
    if (!pagination) return data;
    const start = (pagination.current - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return data.slice(start, end);
  }, [data, pagination]);

  const handleSort = useCallback((key: string) => {
    if (!onSortChange) return;
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig?.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    onSortChange({ key, direction });
  }, [sortConfig, onSortChange]);

  const getRowKey = useCallback((record: T, index: number) => {
    if (typeof rowKey === 'function') {
      return rowKey(record);
    }
    return record[rowKey] || index.toString();
  }, [rowKey]);

  const totalPages = useMemo(() => {
    if (!pagination) return 1;
    return Math.ceil(pagination.total / pagination.pageSize);
  }, [pagination]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <div className="rounded-md border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={cn(
                    "font-semibold text-card-foreground",
                    column.align === 'center' && "text-center",
                    column.align === 'left' && "text-left",
                    column.align === 'right' && "text-right",
                    column.className
                  )}
                  style={{ width: column.width }}
                >
                  {column.sortable ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 font-semibold hover:bg-transparent"
                      onClick={() => handleSort(column.key)}
                    >
                      <span className="ml-2">{column.title}</span>
                      {sortConfig?.key === column.key ? (
                        sortConfig.direction === 'asc' ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 opacity-50" />
                      )}
                    </Button>
                  ) : (
                    column.title
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center py-8 text-muted-foreground">
                  {emptyText}
                </TableCell>
              </TableRow>
            ) : (
              displayData.map((record, index) => {
                const rowProps = onRow?.(record, index) || {};
                return (
                  <TableRow
                    key={getRowKey(record, index)}
                    className={cn("hover:bg-muted/50 transition-colors", rowProps.className)}
                    {...rowProps}
                  >
                    {columns.map((column) => {
                      const value = column.dataIndex ? record[column.dataIndex] : record;
                      const content = column.render 
                        ? column.render(value, record, index)
                        : (value != null ? String(value) : '');
                      
                      return (
                        <TableCell
                          key={column.key}
                          className={cn(
                            column.align === 'center' && "text-center",
                            column.align === 'left' && "text-left",
                            column.align === 'right' && "text-right",
                            column.className
                          )}
                        >
                          {content}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {pagination && onPaginationChange && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 px-2">
          <div className="text-sm text-muted-foreground">
            عرض {Math.min((pagination.current - 1) * pagination.pageSize + 1, pagination.total)} - {Math.min(pagination.current * pagination.pageSize, pagination.total)} من {pagination.total} نتيجة
          </div>
          
          <div className="flex items-center gap-6">
            {pagination.showSizeChanger && pagination.pageSizeOptions && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">عدد الصفوف:</span>
                <Select
                  value={pagination.pageSize.toString()}
                  onValueChange={(value) => onPaginationChange(1, parseInt(value))}
                >
                  <SelectTrigger className="w-[70px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {pagination.pageSizeOptions.map((size) => (
                      <SelectItem key={size} value={size.toString()}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPaginationChange(1, pagination.pageSize)}
                disabled={pagination.current === 1}
                className="h-8 w-8 p-0"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPaginationChange(pagination.current - 1, pagination.pageSize)}
                disabled={pagination.current === 1}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              
              <div className="flex items-center gap-1 mx-2">
                <span className="text-sm text-muted-foreground">صفحة</span>
                <span className="text-sm font-medium">{pagination.current}</span>
                <span className="text-sm text-muted-foreground">من</span>
                <span className="text-sm font-medium">{totalPages}</span>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPaginationChange(pagination.current + 1, pagination.pageSize)}
                disabled={pagination.current === totalPages}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPaginationChange(totalPages, pagination.pageSize)}
                disabled={pagination.current === totalPages}
                className="h-8 w-8 p-0"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}